import UpdateIcon from '@mui/icons-material/Update';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { CopyToClipboard } from '@openx/components-core/lib/CopyToClipboard/CopyToClipboard.tsx';
import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase.tsx';
import { Link } from '@openx/components-core/lib/Link/Link.tsx';
import type { DealError, DealParticipant, SynchronizedWith } from '@openx/types/deal.ts';
import { synchronizationStatuses } from '@openx/types/deal.ts';
import { WarningCircleIcon } from '@phosphor-icons/react/WarningCircle';
import { useTranslation } from 'react-i18next';
import { useGetDspStatus } from '../../hooks/useGetDspStatus/useGetDspStatus.ts';
import { GroupByDealIdButton } from './GroupByDealIdButton.tsx';
import { StatusPill, type StatusPillTone } from './StatusPill.tsx';

type NameCellWithStatusProps = {
  id?: string | null;
  sync?: SynchronizedWith | null;
  deal_participants: DealParticipant[];
  name?: string;
  pathTo: string;
  subRowLabel: string;
  dealStatusLabel?: string;
  dealStatusKey?: string;
  searchPhrase?: string;
  listOfDealIdsToGroup?: string[];
  updateDealIdFilter?: (dealIdFilterValue?: string) => void;
  isOxsSubOrg?: boolean;
  organizationName?: string;
  dealError?: DealError | null;
  dealStatus?: string;
};

export const NameCellWithStatus = ({
  id = '',
  name = '',
  pathTo,
  subRowLabel,
  sync,
  deal_participants,
  dealStatusLabel,
  dealStatusKey,
  searchPhrase,
  listOfDealIdsToGroup = [],
  updateDealIdFilter,
  isOxsSubOrg,
  organizationName,
  dealError,
  dealStatus,
}: NameCellWithStatusProps) => {
  const status = useGetDspStatus({ deal_participants, sync, isOxsSubOrg });
  const { t } = useTranslation();

  const hasBothMessages = !!status.dv360Message && !!status.ttdMessage;

  const dealTone: StatusPillTone | null = (() => {
    const normalizedKey = (dealStatusKey ?? '').toLowerCase();
    const normalizedLabel = (dealStatusLabel ?? '').toLowerCase();

    // Primary: mapped form keys coming from `dealStatusForm`
    switch (normalizedKey) {
      case 'active':
        return 'success';
      case 'publishing':
      case 'goalreached':
        return 'info';
      case 'warning': // Paused
        return 'beige';
      case 'inactive': // Expired
        return 'neutral';
      default:
        break;
    }

    // Fallback: raw label
    switch (normalizedLabel) {
      case 'active':
        return 'success';
      case 'publishing':
      case 'goal reached':
        return 'info';
      case 'paused':
        return 'beige';
      case 'expired':
        return 'neutral';
      case 'error':
        return 'pink';
      default:
        return null;
    }
  })();

  const dspTone: StatusPillTone | null = (() => {
    switch (status.dspStatus) {
      case synchronizationStatuses.SYNCED:
        return 'success';
      case synchronizationStatuses.REJECTED:
        return 'warning';
      case synchronizationStatuses.FAILED:
        return 'error';
      default:
        return null;
    }
  })();

  const dspLabel = (() => {
    switch (status.dspStatus) {
      case synchronizationStatuses.SYNCED:
        return t('Synced');
      case synchronizationStatuses.REJECTED:
        return t('Rejected by DSP');
      case synchronizationStatuses.FAILED:
        return t('Sync failed');
      default:
        return '';
    }
  })();

  const isDealStatusError = dealStatus === 'Error';

  const shouldShowDealStatus = !!dealStatusLabel && !!dealTone;
  const shouldShowDspStatus = status.shouldDisplayStatus && !!dspTone && !!dspLabel;
  const dealEndIcon = (dealStatusKey ?? '').toLowerCase() === 'publishing' ? <UpdateIcon /> : undefined;
  const shouldShowDealErrorTooltip = isDealStatusError && !!dealError;

  return (
    <Box
      sx={{
        ':hover': {
          '.on-hover-visible': {
            visibility: 'visible',
          },
        },
        alignItems: 'center',
        display: 'flex',
        paddingBottom: 0,
        paddingTop: 0,
      }}
    >
      <Box>
        {organizationName && (
          <Typography
            data-test="row-organization-name"
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '10px',
              lineHeight: '12px',
            }}
          >
            {organizationName}
          </Typography>
        )}
        <Link data-test="row-title" to={pathTo}>
          <HighlightedPhrase searchPhrase={searchPhrase}>{name}</HighlightedPhrase>
        </Link>

        <Box
          sx={{ alignItems: 'center', columnGap: 1, display: 'flex', flexWrap: 'wrap', marginTop: 0.2, rowGap: 0.5 }}
          data-test="row-subtitle"
        >
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              display: 'inline',
              whiteSpace: 'nowrap',
            }}
          >
            <HighlightedPhrase searchPhrase={searchPhrase}>{subRowLabel}</HighlightedPhrase>
          </Typography>

          <Typography
            component="span"
            variant="body2Bold"
            sx={{
              whiteSpace: 'nowrap',
            }}
          >
            <HighlightedPhrase searchPhrase={searchPhrase}>{String(id)}</HighlightedPhrase>
          </Typography>

          <GroupByDealIdButton dealId={id} dealIdsToGroup={listOfDealIdsToGroup} onClick={updateDealIdFilter} />
          {!!id && (
            <Box
              component="span"
              sx={{ display: 'inline', marginLeft: -0.5, visibility: 'hidden' }}
              className="on-hover-visible"
            >
              <CopyToClipboard tooltipText={t('Copy External Deal ID to clipboard')} text={id} />
            </Box>
          )}

          {(shouldShowDealStatus || shouldShowDspStatus) && (
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {shouldShowDealStatus && (
                <>
                  <StatusPill
                    data-test="deal-status-pill"
                    label={dealStatusLabel!}
                    tone={dealTone!}
                    endIcon={dealEndIcon}
                  />

                  {shouldShowDealErrorTooltip && (
                    <AlertTooltip
                      color="error.main"
                      title={
                        <Box aria-label={t('error status details tooltip')} data-test="deal-error-tooltip">
                          {!!dealError.timestamp && <Typography variant="body2">{dealError.timestamp}</Typography>}
                          <Typography variant="body2">{dealError.message}</Typography>
                        </Box>
                      }
                      icon={<WarningCircleIcon data-test="tooltip-alert-icon" size={20} />}
                    />
                  )}
                </>
              )}
              {shouldShowDspStatus && (
                <Tooltip
                  title={
                    <Box
                      data-test="dsp-status-tooltip"
                      sx={{
                        width: '100%',
                        maxWidth: '380px',
                      }}
                    >
                      {!!status.dv360Message && (
                        <>
                          <Typography variant="body2">
                            <b>DV360</b> - {status.dv360Message}
                          </Typography>

                          {!!status.dv360ErrorReason && (
                            <Typography variant="body2">{status.dv360ErrorReason}</Typography>
                          )}
                        </>
                      )}

                      {hasBothMessages && <br />}

                      {!!status.ttdMessage && (
                        <Typography variant="body2">
                          <b>TTD</b> - {status.ttdMessage}
                        </Typography>
                      )}
                    </Box>
                  }
                  slotProps={{
                    tooltip: {
                      sx: { maxWidth: '380px' },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-flex' }} data-test={`dsp-status-${name}`}>
                    <StatusPill label={dspLabel} tone={dspTone!} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
